import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  Plus,
  Receipt,
  MoreVertical,
  Edit2,
  Trash2,
  Calendar,
  Tag,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  AlertCircle,
  FileSpreadsheet,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation, useLocale, type Locale } from "./contexts/LocaleContext";
import { Expense } from "./types";
import { authenticatedFetch } from "./config/api";
import { sanitizeInput } from "./utils/sanitize";

export default function Expenses() {
  const t = useTranslation() as any;
  const { locale } = useLocale();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(() => {
    const year = new Date().getFullYear();
    return `${year}-01-01`;
  });
  const [exportEndDate, setExportEndDate] = useState(() => {
    const year = new Date().getFullYear();
    return `${year}-12-31`;
  });
  const [exportLang, setExportLang] = useState<Locale>(locale);
  const [isExporting, setIsExporting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    category: "other",
    date: new Date().toISOString().split("T")[0],
    amount: "",
    currency: "CZK",
    note: "",
  });

  const categories = useMemo(() => [
    { id: "subscriptions", label: t.expenses.categories.subscriptions },
    { id: "consulting", label: t.expenses.categories.consulting },
    { id: "communication", label: t.expenses.categories.communication },
    { id: "education", label: t.expenses.categories.education },
    { id: "equipment", label: t.expenses.categories.equipment },
    { id: "marketing", label: t.expenses.categories.marketing },
    { id: "rent", label: t.expenses.categories.rent },
    { id: "software", label: t.expenses.categories.software },
    { id: "supplies", label: t.expenses.categories.supplies },
    { id: "travel", label: t.expenses.categories.travel },
    { id: "salary", label: t.expenses.categories.salary },
    { id: "taxes", label: t.expenses.categories.taxes },
    { id: "other", label: t.expenses.categories.other },
  ], [t]);

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      setIsLoading(true);
      const response = await authenticatedFetch("/api/expenses");
      if (response.ok) {
        const data = await response.json();
        setExpenses(data);
      }
    } catch (error) {
      console.error("Error fetching expenses:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredExpenses = useMemo(() => {
    return expenses.filter((exp) => {
      const matchesSearch = exp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (exp.note || "").toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === "all" || exp.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [expenses, searchQuery, categoryFilter]);

  const handleOpenModal = (expense?: Expense) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        name: expense.name,
        category: expense.category,
        date: expense.date.split("T")[0],
        amount: expense.amount.toString(),
        currency: expense.currency,
        note: expense.note || "",
      });
    } else {
      setEditingExpense(null);
      setFormData({
        name: "",
        category: "other",
        date: new Date().toISOString().split("T")[0],
        amount: "",
        currency: "CZK",
        note: "",
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.amount) return;

    try {
      setIsSaving(true);
      const method = editingExpense ? "PUT" : "POST";
      const url = editingExpense ? `/api/expenses/${editingExpense.id}` : "/api/expenses";
      
      const response = await authenticatedFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          amount: parseFloat(formData.amount),
        }),
      });

      if (response.ok) {
        await fetchExpenses();
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error("Error saving expense:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t.expenses.deleteConfirm)) return;

    try {
      const response = await authenticatedFetch(`/api/expenses/${id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        setExpenses(prev => prev.filter(e => e.id !== id));
      }
    } catch (error) {
      console.error("Error deleting expense:", error);
    }
  };

  const handleExportToXlsx = async () => {
    try {
      setIsExporting(true);
      let url = `/api/export-expenses-xlsx?lang=${exportLang}`;
      if (exportStartDate) url += `&startDate=${exportStartDate}`;
      if (exportEndDate) url += `&endDate=${exportEndDate}`;

      const response = await authenticatedFetch(url);
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        const filename = `expenses-export-${new Date().toISOString().split("T")[0]}.xlsx`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
        setShowExportModal(false);
      } else {
        const errorData = await response.json();
        alert(errorData.error || "Failed to export expenses");
      }
    } catch (error) {
      console.error("Error exporting to XLSX:", error);
      alert("An error occurred during export");
    } finally {
      setIsExporting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(locale === "ru" ? "ru-RU" : "cs-CZ");
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 px-4 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
            {t.expenses.title}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium">
            {t.expenses.manageTitle}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center justify-center gap-2 px-4 sm:px-6 py-3 sm:py-3.5 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-2xl font-black shadow-sm transition-all hover:bg-slate-50 dark:hover:bg-slate-700 text-sm"
          >
            <FileSpreadsheet size={18} className="text-emerald-500" />
            {t.statistics.exportToExcel}
          </button>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center justify-center gap-2 px-4 sm:px-6 py-3 sm:py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black shadow-lg shadow-indigo-500/25 transition-all hover:scale-[1.02] active:scale-[0.98] text-sm"
          >
            <Plus size={18} />
            {t.expenses.addExpense}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl p-3 sm:p-4 shadow-sm border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row gap-3 sm:gap-4 transition-colors">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 sm:w-5 sm:h-5" />
          <input
            type="text"
            placeholder={t.common.search}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-sm text-slate-900 dark:text-white"
          />
        </div>
        <div className="flex gap-3 sm:gap-4">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="flex-1 md:flex-none px-4 py-2.5 sm:py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-sm text-slate-900 dark:text-white appearance-none cursor-pointer pr-10"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 1rem center",
              backgroundSize: "1.2em",
            }}
          >
            <option value="all">{t.common.all}</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 rounded-[2rem] sm:rounded-[2.5rem] shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden transition-colors">
        <div className="overflow-x-auto custom-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
          <table className="w-full text-left border-collapse min-w-[600px] sm:min-w-[800px]">
            <thead>
              <tr className="bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-700">
                <th className="px-4 sm:px-8 py-4 sm:py-5 text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">{t.common.date}</th>
                <th className="px-4 sm:px-8 py-4 sm:py-5 text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">{t.expenses.expenseName}</th>
                <th className="px-4 sm:px-8 py-4 sm:py-5 text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">{t.expenses.category}</th>
                <th className="px-4 sm:px-8 py-4 sm:py-5 text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">{t.common.amount}</th>
                <th className="px-4 sm:px-8 py-4 sm:py-5 text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">{t.common.actions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-slate-700/50">
              <AnimatePresence mode="popLayout">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={`skeleton-${i}`} className="animate-pulse">
                      <td colSpan={5} className="px-4 sm:px-8 py-4 sm:py-6">
                        <div className="h-4 bg-slate-100 dark:bg-slate-700 rounded-full w-full" />
                      </td>
                    </tr>
                  ))
                ) : filteredExpenses.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 sm:px-8 py-16 sm:py-20 text-center">
                      <div className="flex flex-col items-center">
                        <Receipt className="w-12 h-12 sm:w-16 sm:h-16 text-slate-200 dark:text-slate-700 mb-4" />
                        <p className="text-sm text-slate-400 dark:text-slate-500 font-medium italic">
                          {searchQuery || categoryFilter !== "all" ? t.common.noExpensesFound : t.common.noExpensesFound}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredExpenses.map((exp) => (
                    <motion.tr
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      key={exp.id}
                      className="group hover:bg-slate-50/50 dark:hover:bg-slate-900/30 transition-colors"
                    >
                      <td className="px-4 sm:px-8 py-4 sm:py-6">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-slate-100 dark:bg-slate-900 flex items-center justify-center text-slate-500 dark:text-slate-400">
                            <Calendar size={16} />
                          </div>
                          <span className="text-xs sm:text-sm font-bold text-slate-900 dark:text-white whitespace-nowrap">
                            {formatDate(exp.date)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 sm:px-8 py-4 sm:py-6">
                        <div>
                          <p className="text-xs sm:text-sm font-black text-slate-900 dark:text-white line-clamp-1">{exp.name}</p>
                          {exp.note && <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5 line-clamp-1">{exp.note}</p>}
                        </div>
                      </td>
                      <td className="px-4 sm:px-8 py-4 sm:py-6">
                        <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 text-[8px] sm:text-[10px] font-black uppercase tracking-wider whitespace-nowrap">
                          {categories.find(c => c.id === exp.category)?.label || exp.category}
                        </span>
                      </td>
                      <td className="px-4 sm:px-8 py-4 sm:py-6 text-right">
                        <p className="text-xs sm:text-sm font-black text-slate-900 dark:text-white whitespace-nowrap">
                          {exp.amount.toLocaleString(locale === "ru" ? "ru-RU" : "cs-CZ", { minimumFractionDigits: 2 })} {exp.currency === "CZK" ? "Kč" : exp.currency}
                        </p>
                      </td>
                      <td className="px-4 sm:px-8 py-4 sm:py-6 text-right">
                        <div className="flex items-center justify-end gap-1 sm:gap-2 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleOpenModal(exp)}
                            className="p-2 sm:p-2.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-xl hover:bg-indigo-100 transition-colors"
                          >
                            <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(exp.id)}
                            className="p-2 sm:p-2.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-xl hover:bg-rose-100 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 transition-colors max-h-[95vh] flex flex-col"
            >
              <div className="px-6 sm:px-8 py-4 sm:py-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0">
                <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white">
                  {editingExpense ? t.expenses.editExpense : t.expenses.addExpense}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-400"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSave} className="p-6 sm:p-8 space-y-4 sm:space-y-6 overflow-y-auto custom-scrollbar">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-1.5 sm:space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                      {t.expenses.expenseName}
                    </label>
                    <input
                      required
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: sanitizeInput(e.target.value) }))}
                      className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-sm sm:text-base text-slate-900 dark:text-white font-bold"
                    />
                  </div>
                  <div className="space-y-1.5 sm:space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                      {t.expenses.category}
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-sm sm:text-base text-slate-900 dark:text-white font-bold appearance-none cursor-pointer"
                    >
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5 sm:space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                      {t.expenses.date}
                    </label>
                    <input
                      required
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                      className="w-full max-w-none px-3 sm:px-4 py-3 sm:py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-sm sm:text-base text-slate-900 dark:text-white font-bold"
                    />
                  </div>
                  <div className="space-y-1.5 sm:space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                      {t.expenses.amount}
                    </label>
                    <div className="flex gap-2">
                      <input
                        required
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                        className="flex-1 px-4 sm:px-5 py-3 sm:py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-sm sm:text-base text-slate-900 dark:text-white font-bold"
                      />
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                        className="w-20 sm:w-24 px-2 sm:px-3 py-3 sm:py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-xs sm:text-sm text-slate-900 dark:text-white font-bold"
                      >
                        <option value="CZK">CZK</option>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5 sm:space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    {t.expenses.note}
                  </label>
                  <textarea
                    rows={3}
                    value={formData.note}
                    onChange={(e) => setFormData(prev => ({ ...prev, note: sanitizeInput(e.target.value) }))}
                    className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-sm sm:text-base text-slate-900 dark:text-white font-bold resize-none"
                  />
                </div>

                <div className="flex gap-3 sm:gap-4 pt-4 pb-2">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 py-3 sm:py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-2xl font-black transition-all text-sm sm:text-base"
                  >
                    {t.common.cancel}
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="flex-1 py-3 sm:py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black shadow-lg shadow-indigo-500/25 transition-all disabled:opacity-50 text-sm sm:text-base"
                  >
                    {isSaving ? t.common.saving : t.common.save}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Export Modal */}
      <AnimatePresence>
        {showExportModal && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowExportModal(false)}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="relative bg-white dark:bg-slate-800 rounded-[2rem] sm:rounded-[2.5rem] shadow-2xl w-full max-w-sm overflow-hidden border border-slate-200 dark:border-slate-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6 sm:mb-8">
                  <h3 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white flex items-center gap-2 sm:gap-3">
                    <FileSpreadsheet className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-500" />
                    {t.statistics.exportModal.title}
                  </h3>
                  <button
                    onClick={() => setShowExportModal(false)}
                    className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-400"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-5 sm:space-y-6">
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 sm:mb-3 ml-1">
                      {t.statistics.exportModal.selectRange}
                    </label>
                    <div className="grid grid-cols-1 gap-3 sm:gap-4">
                      <div className="space-y-1 sm:space-y-1.5">
                        <span className="text-[10px] uppercase font-bold text-slate-400 ml-1">
                          {t.statistics.exportModal.from}
                        </span>
                        <input
                          type="date"
                          value={exportStartDate}
                          onChange={(e) => setExportStartDate(e.target.value)}
                          className="w-full px-3 sm:px-4 py-3 sm:py-3.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-sm sm:text-base text-slate-900 dark:text-white font-bold"
                        />
                      </div>
                      <div className="space-y-1 sm:space-y-1.5">
                        <span className="text-[10px] uppercase font-bold text-slate-400 ml-1">
                          {t.statistics.exportModal.to}
                        </span>
                        <input
                          type="date"
                          value={exportEndDate}
                          onChange={(e) => setExportEndDate(e.target.value)}
                          className="w-full px-3 sm:px-4 py-3 sm:py-3.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-sm sm:text-base text-slate-900 dark:text-white font-bold"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 sm:mb-3 ml-1">
                      {t.statistics.exportModal.tableLanguage}
                    </label>
                    <select
                      value={exportLang}
                      onChange={(e) => setExportLang(e.target.value as Locale)}
                      className="w-full px-4 sm:px-5 py-3 sm:py-3.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-sm sm:text-base text-slate-900 dark:text-white font-bold appearance-none cursor-pointer"
                    >
                      <option value="cs">Čeština (CS)</option>
                      <option value="en">English (EN)</option>
                      <option value="ru">Русский (RU)</option>
                    </select>
                  </div>

                  <button
                    onClick={handleExportToXlsx}
                    disabled={isExporting}
                    className="w-full py-3 sm:py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black shadow-lg shadow-indigo-500/25 transition-all flex items-center justify-center gap-2 disabled:opacity-50 text-sm sm:text-base"
                  >
                    {isExporting ? (
                      <>
                        <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t.common.loading}
                      </>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-4 h-4 sm:w-5 sm:h-5" />
                        {t.statistics.exportModal.download}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

